import type { Abi, Address, Hash } from 'viem'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAccount, usePublicClient } from 'wagmi'

import { useWidgetChainScope, type WidgetChainScope } from '../../config'
import { isNativeCurrency, nativeCurrencyId } from '../../config/native'
import { Currency, TokenAmount, WETH, currencyEquals } from '../../sdk'
import { abi as IUniswapV2Router02ABI } from '../../constants/abis/IUniswapV2Router02.json'
import { ROUTER_ADDRESS } from '../../constants'
import {
  MintField as Field,
  useDerivedMintInfo,
  useMintActionHandlers,
  useMintState,
} from '../../domain/liquidity'
import {
  clearLiquidityAddFormDraft,
  persistLiquidityAddFormDraft,
  readLiquidityAddFormDraft,
} from '../../domain/liquidity/liquidityFormDraft'
import { readMobileLiquiditySession } from '../../domain/liquidity/mobileLiquiditySession'
import { buildLiquidityChangeInvalidateCalls } from '../../domain/multicall'
import { submitSimulatedContractWrite } from '../../domain/wallet/submitSimulatedContractWrite'
import {
  endWalletSigning,
  toWalletUserFacingError,
  type WalletWriteProgressPhase,
} from '../../domain/wallet/walletSigningFlow'
import { markWalletReconnectCooldown } from '../../domain/wallet/walletReconnectCooldown'
import { nutrimaticLabels } from '@/nutrimatic'
import { useTransactionAdder } from '../../domain/transactions'
import { useCurrency } from '../../domain/tokens'
import { useAppWeb3 } from '../../hooks'
import { ApprovalState, useApproveCallback } from '../../hooks/useApproveCallback'
import { useOpenConnectModal } from '@/wallet'
import { useIsExpertMode, useUserDeadline, useUserSlippageTolerance, usePairAdder } from '../../state/user/hooks'
import type { WidgetRouteIntent } from '../../widgets/navigation/widgetRouteIntent'
import { trackLiquidityPair } from './trackLiquidityPair'
import { useLiquidityMobileSession } from './useLiquidityMobileSession'
import { calculateSlippageAmount } from '../../utils'
import { currencyId } from '../../utils/currencyId'
import { txSubmitDebug, txSubmitWarn } from '../../utils/debugTxSubmit'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { calculateGasMarginViem } from '../../utils/swapParametersViem'
import { wrappedCurrency } from '../../utils/wrappedCurrency'

const ROUTER_ABI = IUniswapV2Router02ABI as Abi

export function useAddLiquidityController(
  resolveRoute: (intent: WidgetRouteIntent) => string,
  chainScope?: WidgetChainScope,
) {
  const navigate = useNavigate()
  const { token0, token1 } = useParams<{ token0?: string; token1?: string }>()
  const { chainId } = useWidgetChainScope(chainScope)
  const { account, library } = useAppWeb3()
  const { connector } = useAccount()
  const publicClient = usePublicClient()
  const openConnectModal = useOpenConnectModal()
  const expertMode = useIsExpertMode()
  const [deadline] = useUserDeadline()
  const [allowedSlippage] = useUserSlippageTolerance()

  const currencyA = useCurrency(token0)
  const currencyB = useCurrency(token1)

  const { independentField, typedValue, otherTypedValue } = useMintState()
  const {
    dependentField,
    currencies,
    pair,
    pairState,
    currencyBalances,
    parsedAmounts,
    price,
    noLiquidity,
    liquidityMinted,
    poolTokenPercentage,
    error,
  } = useDerivedMintInfo(currencyA ?? undefined, currencyB ?? undefined)
  const { onFieldAInput, onFieldBInput } = useMintActionHandlers(noLiquidity)

  const [showConfirm, setShowConfirm] = useState(false)
  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [walletProgressPhase, setWalletProgressPhase] = useState<WalletWriteProgressPhase | null>(null)
  const [walletErrorMessage, setWalletErrorMessage] = useState<string | null>(null)
  const [txHash, setTxHash] = useState('')

  const applyWalletRejected = useCallback((message: string) => {
    endWalletSigning()
    markWalletReconnectCooldown()
    setAttemptingTxn(false)
    setWalletProgressPhase(null)
    setWalletErrorMessage(message)
  }, [])

  const {
    writeSessionForReview,
    patchSessionWallet,
    patchSessionSubmitted,
    clearSession,
    completeSession,
    trackInFlight,
  } = useLiquidityMobileSession({
    kind: 'add',
    account,
    chainId,
    token0,
    token1,
    library,
    attemptingTxn,
    walletProgressPhase,
    setAttemptingTxn,
    setWalletProgressPhase,
    setWalletErrorMessage,
    setShowConfirm,
    setTxHash,
    onWalletRejected: applyWalletRejected,
  })

  const draftRestoredRef = useRef(false)

  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: noLiquidity ? otherTypedValue : (parsedAmounts[dependentField]?.toSignificant(6) ?? ''),
  }

  const maxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => ({
      ...accumulator,
      [field]: maxAmountSpend(currencyBalances[field]),
    }),
    {},
  )

  const atMaxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => ({
      ...accumulator,
      [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
    }),
    {},
  )

  const [approvalA, approveACallback] = useApproveCallback(parsedAmounts[Field.CURRENCY_A], ROUTER_ADDRESS)
  const [approvalB, approveBCallback] = useApproveCallback(parsedAmounts[Field.CURRENCY_B], ROUTER_ADDRESS)
  const addTransaction = useTransactionAdder()
  const addPair = usePairAdder()

  const oneCurrencyIsWETH = Boolean(
    chainId &&
      ((currencyA && currencyEquals(currencyA, WETH[chainId])) ||
        (currencyB && currencyEquals(currencyB, WETH[chainId]))),
  )

  const onAdd = useCallback(async () => {
    if (!chainId || !account || !publicClient || !connector) {
      txSubmitWarn('liquidity add skipped: missing chain, account, or clients', {
        hasChainId: Boolean(chainId),
        hasAccount: Boolean(account),
        hasPublicClient: Boolean(publicClient),
        hasConnector: Boolean(connector),
      })
      return
    }

    const { [Field.CURRENCY_A]: parsedAmountA, [Field.CURRENCY_B]: parsedAmountB } = parsedAmounts
    if (!parsedAmountA || !parsedAmountB || !currencyA || !currencyB) {
      txSubmitWarn('liquidity add skipped: missing parsed amounts or currencies', {})
      return
    }

    txSubmitDebug('liquidity add pipeline begin', { chainId, account })

    const amountsMin = {
      [Field.CURRENCY_A]: calculateSlippageAmount(parsedAmountA, noLiquidity ? 0 : allowedSlippage)[0],
      [Field.CURRENCY_B]: calculateSlippageAmount(parsedAmountB, noLiquidity ? 0 : allowedSlippage)[0],
    }

    const deadlineFromNow = Math.ceil(Date.now() / 1000) + deadline

    setWalletErrorMessage(null)
    setAttemptingTxn(true)
    patchSessionWallet()
    try {
      let hash: Hash

      if (chainId != null && (isNativeCurrency(currencyA, chainId) || isNativeCurrency(currencyB, chainId))) {
        txSubmitDebug('liquidity add path', { kind: 'addLiquidityETH' })
        const tokenBIsETH = isNativeCurrency(currencyB, chainId)
        const tokenAddr = wrappedCurrency(tokenBIsETH ? currencyA : currencyB, chainId)?.address as Hash
        const viemArgs = [
          tokenAddr,
          BigInt((tokenBIsETH ? parsedAmountA : parsedAmountB).raw.toString()),
          BigInt(amountsMin[tokenBIsETH ? Field.CURRENCY_A : Field.CURRENCY_B].toString()),
          BigInt(amountsMin[tokenBIsETH ? Field.CURRENCY_B : Field.CURRENCY_A].toString()),
          account as Hash,
          BigInt(deadlineFromNow),
        ] as const
        const valueWei = BigInt((tokenBIsETH ? parsedAmountB : parsedAmountA).raw.toString())

        const { request } = await publicClient.simulateContract({
          address: ROUTER_ADDRESS as Hash,
          abi: ROUTER_ABI,
          functionName: 'addLiquidityETH',
          args: [...viemArgs],
          account: account as Hash,
          value: valueWei,
        } as any)

        const gas = request.gas ? calculateGasMarginViem(request.gas) : undefined
        hash = await submitSimulatedContractWrite({
          request: {
            address: request.address,
            abi: request.abi,
            functionName: request.functionName,
            args: request.args as readonly unknown[],
          },
          account: account as Address,
          chainId,
          connector,
          gas,
          value: valueWei,
          onPhase: setWalletProgressPhase,
        })
      } else {
        txSubmitDebug('liquidity add path', { kind: 'addLiquidity' })
        const addrA = wrappedCurrency(currencyA, chainId)?.address as Hash
        const addrB = wrappedCurrency(currencyB, chainId)?.address as Hash
        const viemArgs = [
          addrA,
          addrB,
          BigInt(parsedAmountA.raw.toString()),
          BigInt(parsedAmountB.raw.toString()),
          BigInt(amountsMin[Field.CURRENCY_A].toString()),
          BigInt(amountsMin[Field.CURRENCY_B].toString()),
          account as Hash,
          BigInt(deadlineFromNow),
        ] as const

        const { request } = await publicClient.simulateContract({
          address: ROUTER_ADDRESS as Hash,
          abi: ROUTER_ABI,
          functionName: 'addLiquidity',
          args: [...viemArgs],
          account: account as Hash,
        } as any)

        const gas = request.gas ? calculateGasMarginViem(request.gas) : undefined
        hash = await submitSimulatedContractWrite({
          request: {
            address: request.address,
            abi: request.abi,
            functionName: request.functionName,
            args: request.args as readonly unknown[],
          },
          account: account as Address,
          chainId,
          connector,
          gas,
          onPhase: setWalletProgressPhase,
        })
      }

      setAttemptingTxn(false)
      setWalletProgressPhase(null)
      setShowConfirm(false)

      addTransaction(
        { hash },
        {
          summary:
            'Add ' +
            parsedAmounts[Field.CURRENCY_A]?.toSignificant(3) +
            ' ' +
            currencies[Field.CURRENCY_A]?.symbol +
            ' and ' +
            parsedAmounts[Field.CURRENCY_B]?.toSignificant(3) +
            ' ' +
            currencies[Field.CURRENCY_B]?.symbol,
          multicallInvalidateCalls:
            account && chainId
              ? buildLiquidityChangeInvalidateCalls(chainId, account, currencyA, currencyB)
              : undefined,
        },
      )

      trackLiquidityPair(addPair, chainId, currencyA, currencyB, pair)

      patchSessionSubmitted(hash)
      setTxHash(hash)
    } catch (error: unknown) {
      endWalletSigning()
      markWalletReconnectCooldown()
      setAttemptingTxn(false)
      setWalletProgressPhase(null)
      setWalletErrorMessage(toWalletUserFacingError(error))
      txSubmitWarn('liquidity add pipeline error', { error: String(error) })
    }
  }, [
    account,
    addPair,
    addTransaction,
    allowedSlippage,
    chainId,
    connector,
    currencies,
    currencyA,
    currencyB,
    deadline,
    noLiquidity,
    parsedAmounts,
    pair,
    patchSessionWallet,
    patchSessionSubmitted,
    publicClient,
  ])

  useLayoutEffect(() => {
    if (draftRestoredRef.current || !chainId) return
    if (readMobileLiquiditySession('add')) return

    const draft = readLiquidityAddFormDraft(chainId)
    if (!draft) return

    if (draft.token0 !== token0 || draft.token1 !== token1) {
      navigate(resolveRoute({ type: 'add', token0: draft.token0, token1: draft.token1 }))
      return
    }

    if (draft.independentField === Field.CURRENCY_A) {
      onFieldAInput(draft.typedValue)
      if (draft.otherTypedValue) onFieldBInput(draft.otherTypedValue)
    } else {
      onFieldBInput(draft.typedValue)
      if (draft.otherTypedValue) onFieldAInput(draft.otherTypedValue)
    }
    draftRestoredRef.current = true
  }, [chainId, navigate, onFieldAInput, onFieldBInput, resolveRoute, token0, token1])

  useEffect(() => {
    if (!chainId || !token0 || !token1) return
    persistLiquidityAddFormDraft({
      chainId,
      token0,
      token1,
      independentField,
      typedValue,
      otherTypedValue,
    })
  }, [chainId, independentField, otherTypedValue, token0, token1, typedValue])

  const handleCurrencyASelect = useCallback(
    (nextCurrency: Currency) => {
      if (!chainId) return
      const newCurrencyIdA = currencyId(nextCurrency, chainId)
      if (newCurrencyIdA === token1) {
        navigate(resolveRoute({ type: 'add', token0: token1, token1: token0 }))
      } else {
        navigate(resolveRoute({ type: 'add', token0: newCurrencyIdA, token1 }))
      }
    },
    [chainId, token0, token1, navigate, resolveRoute],
  )

  const handleCurrencyBSelect = useCallback(
    (nextCurrency: Currency) => {
      if (!chainId) return
      const newCurrencyIdB = currencyId(nextCurrency, chainId)
      if (token0 === newCurrencyIdB) {
        if (token1) {
          navigate(resolveRoute({ type: 'add', token0: token1, token1: newCurrencyIdB }))
        } else {
          navigate(resolveRoute({ type: 'add', token1: newCurrencyIdB }))
        }
      } else {
        navigate(
          resolveRoute({
            type: 'add',
            token0: token0 ? token0 : nativeCurrencyId(chainId),
            token1: newCurrencyIdB,
          }),
        )
      }
    },
    [chainId, token0, token1, navigate, resolveRoute],
  )

  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false)
    setAttemptingTxn(false)
    if (txHash) {
      onFieldAInput('')
      completeSession()
      clearLiquidityAddFormDraft()
    }
    setTxHash('')
    setWalletErrorMessage(null)
    setWalletProgressPhase(null)
  }, [completeSession, onFieldAInput, txHash])

  const cancelLiquidityWalletWait = useCallback(() => {
    endWalletSigning()
    markWalletReconnectCooldown()
    clearSession()
    setAttemptingTxn(false)
    setWalletProgressPhase(null)
    setWalletErrorMessage(nutrimaticLabels.mobileSimple.liquidityOperationCancelled)
  }, [clearSession])

  const dismissCancelledLiquidity = useCallback(() => {
    markWalletReconnectCooldown()
    endWalletSigning()
    clearSession()
    setAttemptingTxn(false)
    setWalletProgressPhase(null)
    setWalletErrorMessage(null)
    setShowConfirm(false)
  }, [clearSession])

  const retryLiquidityAfterError = useCallback(() => {
    endWalletSigning()
    setWalletErrorMessage(null)
    setAttemptingTxn(false)
    setWalletProgressPhase(null)
    setShowConfirm(true)
  }, [])

  const handleReviewDismiss = useCallback(() => {
    if (attemptingTxn || walletProgressPhase) {
      cancelLiquidityWalletWait()
      return
    }
    handleDismissConfirmation()
  }, [attemptingTxn, cancelLiquidityWalletWait, handleDismissConfirmation, walletProgressPhase])

  const handleMaxA = useCallback(() => {
    onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
  }, [maxAmounts, onFieldAInput])

  const handleMaxB = useCallback(() => {
    onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
  }, [maxAmounts, onFieldBInput])

  const handleSupplyAction = useCallback(() => {
    writeSessionForReview()
    if (expertMode) {
      void trackInFlight(onAdd())
      return
    }
    setShowConfirm(true)
  }, [expertMode, onAdd, trackInFlight, writeSessionForReview])

  const pendingText = `Supplying ${parsedAmounts[Field.CURRENCY_A]?.toSignificant(6)} ${
    currencies[Field.CURRENCY_A]?.symbol
  } and ${parsedAmounts[Field.CURRENCY_B]?.toSignificant(6)} ${currencies[Field.CURRENCY_B]?.symbol}`

  const confirmAdd = useCallback(() => {
    void trackInFlight(Promise.resolve(onAdd()))
  }, [onAdd, trackInFlight])

  return {
    Field,
    account,
    allowedSlippage,
    approvalA,
    approvalB,
    approveACallback,
    approveBCallback,
    attemptingTxn,
    atMaxAmounts,
    cancelLiquidityWalletWait,
    chainId,
    confirmAdd,
    currencies,
    currencyA,
    currencyB,
    token0,
    token1,
    error,
    dismissCancelledLiquidity,
    expertMode,
    formattedAmounts,
    handleCurrencyASelect,
    handleCurrencyBSelect,
    handleDismissConfirmation,
    handleReviewDismiss,
    handleMaxA,
    handleMaxB,
    handleSupplyAction,
    isValid: !error,
    liquidityMinted,
    maxAmounts,
    noLiquidity,
    onAdd,
    onFieldAInput,
    onFieldBInput,
    oneCurrencyIsWETH,
    openConnectModal,
    pair,
    pairState,
    parsedAmounts,
    pendingText,
    poolTokenPercentage,
    price,
    retryLiquidityAfterError,
    showConfirm,
    setShowConfirm,
    txHash,
    walletErrorMessage,
    walletProgressPhase,
  }
}

export type AddLiquidityControllerResult = ReturnType<typeof useAddLiquidityController>
